import { AuthObject } from '@white-label-types/auth';
import { datadogRum } from '@datadog/browser-rum';
import { Route } from 'vue-router';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { isDedicatedFlow, getUserPermissions, isMultiChannelPartnerEnabled } from './dedicated-flow';
import { useNuxtApp } from '@nuxt/bridge/dist/runtime';

interface AuthContext {
    redirect: (path: string) => void;
    $config: Record<string, string>;
    route: Route;
    $auth: AuthObject;
}

const AUTHORISED_ROLES = [
    'cavu-admin',
    'cavu-cs',
    'partner-admin',
    'partner-order-editor',
] as const;

const AUTHORISED_PERMISSIONS = [
    'adm:dedicated-wl:read', // used for admin extranet users only to bypass needing channel token
] as const;

/**
 * Shared auth0 middleware logic for dedicated flows
 * @param context The auth context containing redirect, config, route and auth
 * @param routeName Optional route name to check against (e.g. CHECKOUT)
 */
export const handleDedicatedAuth0 = async ({
    redirect,
    route,
    $auth,
}: AuthContext, routeName?: string): Promise<void> => {
    // Check if we're in a dedicated flow for the specified route or any dedicated flow
    if (isDedicatedFlow(route, routeName)) {
        // Check if logged in with correct strategy - partner
        if ($auth.user) {
            // check the user has authorised roles to access dedicated flow
            const userRoles: string[] = $auth.user['https://user_metadata/roles'] || [];
            const isUserAuthorised = userRoles.some(role => {
              const match = (AUTHORISED_ROLES as readonly string[]).some(authorisedRole => role.startsWith(authorisedRole));
              return match;
            });

            // restrict unauthorised users access to non-dedicated WL only
            if (!isUserAuthorised) {
                datadogRum.addError(
                    new Error('Auth isDedicatedFlow:', { cause: new Error('User is not authorised') }),
                    {
                        file: 'white-label-helper > dedicated-flow > isDedicatedFlow()',
                        route: route.name || '',
                        user: $auth.user,
                    }
                );
                // redirect and keep logged in
                redirect('/');
                return;
            }

            // Check the user has the correct Permissions to access dedicated flow
            // Only Admin extranet users can bypass the channel token check
            const token: string | null = await $auth.getToken(); // bearer token contains the user's permissions
            let userHasPermission: boolean = false;
            if (token) {
                userHasPermission = AUTHORISED_PERMISSIONS.some(permission =>
                    getUserPermissions(token).includes(permission)
                );
            }

            // if the user does have permissions, it means they've come from Admin extranet and don't need to check channel token
            /** CUS-2972 */
            // Admin extranet should be able to have a partners channel token with the new modal selector
            // Code refactor:
            // 1. First check channel token
            // 2. Then check for user permissions
            // User has admin extranet permissions — no channel token check needed
            if (userHasPermission) {
                return;
            }

            // check the user has the correct channel token matching the partner
            const userChannelToken: string = isMultiChannelPartnerEnabled()
                ? ((): string => {
                    const nuxtApp = useNuxtApp();
                    return nuxtApp['$cookies'].get(`auth0.${nuxtApp['$config']['AUTH0_PARTNER_CLIENT_ID']}.channelToken`) || $auth.channelToken;
                })()
                : $auth.user['https://user_metadata/channel_token'];

            const appChannelToken: string = getAppVariable('channel.token');
            const isChannelAuthorised: boolean = !!(userChannelToken && appChannelToken) && (userChannelToken === appChannelToken);

            if (isChannelAuthorised) {
                return;
            }

            datadogRum.addError(new Error('Dedicated flow - User channel token does not match'), {
                file: 'dedicated-auth.ts helper',
                userChannelToken,
                appChannelToken,
                isChannelAuthorised
            });
            await $auth.logout();
        } else {
            // un-authenticated / un-authorised
            // log to datadog
            datadogRum.addError(
                new Error('Auth isDedicatedFlow:', { cause: new Error('User is un-authenticated / un-authorised') }),
                {
                    file: 'white-label-helper > dedicated-flow > isDedicatedFlow()',
                    route: route.name || '',
                    user: $auth.user
                }
            );
            redirect('/');
        }
    }
};
